<script setup>
import { useForm } from '@inertiajs/vue3'
import { useToast } from 'primevue/usetoast'
import { useI18n } from 'vue-i18n'
import FormInput from '@/components/form/FormInput.vue'
import { useIdCodeValidation } from '@/composables/useIdCodeValidation'

const props = defineProps({
  user: { type: Object, required: true },
})

const { t } = useI18n()
const toast = useToast()

// RED-286: precognitive, so the write-only Isikukood field is checked against
// the server rule before the profile is saved.
const form = useForm('put', route('admin.user.profile', props.user.id), {
  firstname: props.user.firstname ?? '',
  lastname: props.user.lastname ?? '',
  phone: props.user.phone ?? '',
  profession_level: props.user.profession_level ?? '',
  profession_no: props.user.profession_no ?? '',
  land_appraiser: props.user.land_appraiser ?? '',
  is_member_ekhy: !!props.user.is_member_ekhy,
  is_member_ekmk: !!props.user.is_member_ekmk,
  // RED-286: write-only. Blank keeps the stored isikukood; it is never shown.
  id_code: '',
})

useIdCodeValidation(form)

const submit = () => {
  form.put(route('admin.user.profile', props.user.id), {
    preserveScroll: true,
    onSuccess: () => {
      form.id_code = ''
      toast.add({
        severity: 'success',
        summary: t('messages.updated'),
        detail: t('messages.admin.user.profile_updated_success'),
        life: 3000,
      })
    },
    onError: () => {
      toast.add({
        severity: 'error',
        summary: t('messages.error'),
        detail: t('messages.admin.user.profile_updated_error'),
        life: 4000,
      })
    },
  })
}
</script>

<template>
  <Card>
    <template #title>{{ $t('messages.admin.user.tabs.profile') }}</template>
    <template #content>
      <Fluid>
        <form class="grid grid-cols-1 md:grid-cols-2 gap-4" @submit.prevent="submit">
          <div>
            <label class="text-sm text-gray-600">{{ $t('messages.admin.user.firstname') }}</label>
            <FormInput v-model="form.firstname" :form="form" field="firstname" />
          </div>
          <div>
            <label class="text-sm text-gray-600">{{ $t('messages.admin.user.lastname') }}</label>
            <FormInput v-model="form.lastname" :form="form" field="lastname" />
          </div>
          <div>
            <label class="text-sm text-gray-600">{{ $t('messages.admin.user.phone') }}</label>
            <FormInput v-model="form.phone" :form="form" field="phone" />
          </div>
          <div>
            <label class="text-sm text-gray-600">{{ $t('messages.admin.user.profession_level') }}</label>
            <FormInput v-model="form.profession_level" :form="form" field="profession_level" />
          </div>
          <div>
            <label class="text-sm text-gray-600">{{ $t('messages.admin.user.profession_no') }}</label>
            <FormInput v-model="form.profession_no" :form="form" field="profession_no" />
          </div>
          <div>
            <label class="text-sm text-gray-600">{{ $t('messages.admin.user.land_appraiser') }}</label>
            <FormInput v-model="form.land_appraiser" :form="form" field="land_appraiser" />
          </div>
          <div>
            <label class="text-sm text-gray-600">{{ $t('messages.admin.user.id_code') }}</label>
            <FormInput
              v-model="form.id_code"
              :form="form"
              field="id_code"
              :placeholder="$t('messages.admin.user.id_code_placeholder')"
              live-validate
              data-testid="profile-id-code"
            >
              <template #hint="{ valid }">
                <span data-testid="profile-id-code-status">
                  <i
                    :class="user.has_id_code ? 'pi pi-check-circle text-green-600' : 'pi pi-minus-circle text-gray-400'"
                    class="text-xs mr-1"
                  />
                  {{ user.has_id_code ? $t('messages.admin.user.id_code_is_set') : $t('messages.admin.user.id_code_not_set') }}.
                  <span :class="{ 'text-green-700': valid }">
                    {{ valid ? $t('messages.admin.user.id_code_valid') : $t('messages.admin.user.id_code_keep_hint') }}
                  </span>
                </span>
              </template>
            </FormInput>
          </div>
          <div class="hidden md:block" />
          <div class="flex items-center gap-2">
            <Checkbox v-model="form.is_member_ekhy" :binary="true" inputId="member-ekhy" />
            <label for="member-ekhy" class="text-sm">EKHÜ</label>
          </div>
          <div class="flex items-center gap-2">
            <Checkbox v-model="form.is_member_ekmk" :binary="true" inputId="member-ekmk" />
            <label for="member-ekmk" class="text-sm">EKMK</label>
          </div>
          <div class="md:col-span-2 flex justify-end">
            <Button type="submit" :disabled="form.processing" :loading="form.processing" class="w-auto!">
              {{ $t('messages.save') }}
            </Button>
          </div>
        </form>
      </Fluid>
    </template>
  </Card>
</template>
